// Shared formatting helpers for the analytics module.
// Dedupes the formatNumber / formatDate logic that was copy-pasted across views.

/** Locale-grouped integer/float, or '--' for null/undefined. */
export function formatNumber(val) {
  if (val == null || val === '') return '--'
  const n = Number(val)
  if (Number.isNaN(n)) return '--'
  return n.toLocaleString()
}

/** Compact number for chart axes / tight spaces: 1234 -> "1k", 1500000 -> "2M". */
export function formatCompact(val) {
  const n = Number(val) || 0
  const abs = Math.abs(n)
  if (abs >= 1_000_000) return (n / 1_000_000).toFixed(abs >= 10_000_000 ? 0 : 1) + 'M'
  if (abs >= 1_000) return (n / 1_000).toFixed(abs >= 10_000 ? 0 : 1) + 'k'
  return String(n)
}

/** Human date (e.g. "3 Jun 2026"); returns '--' for empty, raw string if unparseable. */
export function formatDate(val, opts = { year: 'numeric', month: 'short', day: 'numeric' }) {
  if (!val) return '--'
  try {
    if (val.toDate) return val.toDate().toLocaleDateString(undefined, opts)
    const d = new Date(val)
    return Number.isNaN(d.getTime()) ? String(val) : d.toLocaleDateString(undefined, opts)
  } catch {
    return String(val)
  }
}

/** Date + time (used in report history / schedule tables). */
export function formatDateTime(val) {
  if (!val) return '--'
  try {
    if (val.toDate) return val.toDate().toLocaleString()
    const d = new Date(val)
    return Number.isNaN(d.getTime()) ? String(val) : d.toLocaleString()
  } catch {
    return String(val)
  }
}

/**
 * Turn an ISO-week label ("2026-W23") into a short, readable label for chart axes.
 * Falls back to the raw value if it isn't an ISO-week string.
 */
export function formatWeekLabel(week) {
  if (typeof week !== 'string') return String(week ?? '')
  const m = week.match(/^(\d{4})-W(\d{1,2})$/)
  if (!m) return week
  return `W${parseInt(m[2], 10)}`
}

// ---------------------------------------------------------------------------
// Period / ISO-week helpers for the analytics toolbar and trend chart.
// Dates here are calendar days (YYYY-MM-DD strings from the range picker), so
// local-midnight Date objects are the right representation — no timezone math.

const MONTHS = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec']
const dayMonth = (d) => `${d.getDate()} ${MONTHS[d.getMonth()]}`
const dayMonthYear = (d) => `${dayMonth(d)} ${d.getFullYear()}`

function parseDay(s) {
  const m = typeof s === 'string' && s.match(/^(\d{4})-(\d{2})-(\d{2})$/)
  return m ? new Date(+m[1], +m[2] - 1, +m[3]) : null
}

/** ISO-week key ("2026-W31") -> { start: Monday, end: Sunday } as local-midnight Dates. */
export function isoWeekRange(week) {
  const m = typeof week === 'string' && week.match(/^(\d{4})-W(\d{1,2})$/)
  if (!m) return null
  const year = +m[1]
  const jan4 = new Date(year, 0, 4) // ISO week 1 always contains 4 January
  const week1Monday = new Date(year, 0, 4 - ((jan4.getDay() + 6) % 7))
  const start = new Date(week1Monday)
  start.setDate(week1Monday.getDate() + (+m[2] - 1) * 7)
  const end = new Date(start)
  end.setDate(start.getDate() + 6)
  return { start, end }
}

/** "2026-W31" -> "27 Jul – 2 Aug" (what the PDF report shows; operators think in dates). */
export function formatWeekRangeLabel(week) {
  const r = isoWeekRange(week)
  if (!r) return formatWeekLabel(week)
  return `${dayMonth(r.start)} – ${dayMonth(r.end)}`
}

/**
 * How many days of an ISO week fall inside the selected range. A week with fewer
 * than 7 covered days is *partial* — its total looks like a collapse when it is
 * really just fewer days, so charts must mark it rather than draw it as a trend.
 */
export function weekCoverage(week, rangeStart, rangeEnd) {
  const r = isoWeekRange(week)
  const rs = parseDay(rangeStart)
  const re = parseDay(rangeEnd)
  if (!r || !rs || !re) return { days: 7, partial: false }
  const from = r.start > rs ? r.start : rs
  const to = r.end < re ? r.end : re
  const days = to < from ? 0 : Math.round((to - from) / 86400000) + 1
  return { days, partial: days < 7 }
}

/** "2026-08-01","2026-08-31" -> "1 – 31 Aug 2026"; across months/years spells both ends. */
export function formatPeriodLabel(start, end) {
  const s = parseDay(start)
  const e = parseDay(end)
  if (!s || !e) return ''
  if (s.getFullYear() !== e.getFullYear()) return `${dayMonthYear(s)} – ${dayMonthYear(e)}`
  if (s.getMonth() !== e.getMonth()) return `${dayMonth(s)} – ${dayMonthYear(e)}`
  return `${s.getDate()} – ${dayMonthYear(e)}`
}

/** ISO-week key ("2026-W36") for a local-midnight Date — inverse of isoWeekRange. */
export function isoWeekKey(d) {
  const date = new Date(d.getFullYear(), d.getMonth(), d.getDate())
  date.setDate(date.getDate() - ((date.getDay() + 6) % 7) + 3) // Thursday decides the ISO year
  const isoYear = date.getFullYear()
  const jan4 = new Date(isoYear, 0, 4)
  const week = 1 + Math.round(((date - jan4) / 86400000 - 3 + ((jan4.getDay() + 6) % 7)) / 7)
  return `${isoYear}-W${String(week).padStart(2, '0')}`
}

/** Metres → "1,284 m" below 10 km, else "12.3 km". null/undefined → "". */
export function formatDistance(m) {
  if (m == null || !Number.isFinite(Number(m))) return ''
  const n = Number(m)
  if (n < 10000) return `${Math.round(n).toLocaleString('en-US')} m`
  return `${(n / 1000).toFixed(1)} km`
}
